import React from 'react';
import { Check, ArrowRight, Star, Users, Target, Clock, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const LeadershipGrowthMastery = () => {
  return (
    <div className="bg-exiser-light min-h-screen">
      {/* Hero */}
      <div className="bg-exiser-primary text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <Link to="/categories" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 uppercase tracking-wider text-xs font-bold transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Programmes
          </Link>
          <div className="inline-flex items-center gap-2 bg-exiser-accent/20 border border-exiser-accent/60 px-3 py-1 rounded-full mb-4">
            <Star className="w-4 h-4 text-exiser-accent fill-current" />
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-exiser-accent">
              Leadership Programme • 2 Days
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-4">
            High-Performance Leadership
          </h1>
          <p className="text-sm md:text-base text-purple-100 max-w-3xl font-sans">
            A strategic high-performance leadership programme designed for Agency Leaders,
            Managers, and Business Associates to build powerful teams, drive performance,
            and achieve consistent recruitment and explosive sales growth.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.2em]">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Duration: 2 Days</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>Ideal For: Leaders / Managers / BAs</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              <span>Fees: ₹4,999</span>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/categories#leadership-growth-mastery"
              className="inline-flex items-center gap-2 bg-exiser-accent text-exiser-primary px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] rounded-lg hover:bg-white shadow-lg"
            >
              Enroll From Programmes Page
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 border border-white/60 text-white px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] rounded-lg hover:bg-white hover:text-exiser-primary"
            >
              Book Leadership Call
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-12 md:py-16 space-y-10">
        {/* Who it's for */}
        <section className="bg-white rounded-2xl shadow-md border border-purple-100 p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold uppercase text-exiser-primary mb-4">
            Who Is This Programme For?
          </h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700 font-sans">
            <ul className="space-y-2">
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
                Agency Leaders & Sales Managers
              </li>
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
                Business Associates & Team Builders
              </li>
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
                Leaders seeking team performance momentum
              </li>
            </ul>
            <ul className="space-y-2">
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
                Leaders wanting consistent recruitment system
              </li>
              <li className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
                Professionals aiming for digital leadership influence
              </li>
            </ul>
          </div>
        </section>

        {/* Key Outcomes */}
        <section className="grid md:grid-cols-2 gap-6">
          <OutcomeCard
            title="1. Consistent Recruitment & Team Building"
            items={[
              'Daily-Weekly-Monthly advisor recruitment model',
              'Influence psychology for attracting advisors',
              'Recruitment scripts & digital attraction funnel',
            ]}
          />
          <OutcomeCard
            title="2. Advisor Activation & Sales Performance"
            items={[
              '30-Day activation blueprint for new advisors',
              'Sales conversion, presentation & objection handling',
              'System to create high-performing independent advisors',
            ]}
          />
          <OutcomeCard
            title="3. Leadership Psychology & Team Momentum"
            items={[
              'Influence, culture, and team direction models',
              'Framework for handling low performance & conflicts',
              'Neuro-leadership transformation and resilience training',
            ]}
          />
          <OutcomeCard
            title="4. Digital Leadership & Legacy-Building"
            items={[
              'Digital recruitment & leadership branding model',
              '90-Day scalable growth system implementation',
              'Money mindset and millionaire leadership habits',
            ]}
          />
        </section>

        {/* CTA */}
        <section className="bg-exiser-primary rounded-2xl p-6 md:p-8 text-white flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-xl md:text-2xl font-bold uppercase mb-2">
              Ready to Become a High-Impact Leader?
            </h3>
            <p className="text-sm text-purple-100 max-w-xl font-sans">
              This 2-day intensive training empowers you to build teams, lead with influence, and
              create a legacy of transformation and performance.
            </p>
          </div>
          <Link
            to="/categories#leadership-growth-mastery"
            className="inline-flex items-center gap-2 bg-exiser-accent text-exiser-primary px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] rounded-lg hover:bg-white shadow-lg"
          >
            Enroll From Programmes Page
            <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </div>
    </div>
  );
};

const OutcomeCard = ({ title, items }) => (
  <div className="bg-white rounded-2xl border border-purple-100 p-6 shadow-sm">
    <h3 className="text-lg font-bold mb-3">{title}</h3>
    <ul className="space-y-2 text-sm text-gray-700 font-sans">
      {items.map((item, idx) => (
        <li key={idx} className="flex gap-2">
          <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
          {item}
        </li>
      ))}
    </ul>
  </div>
);

export default LeadershipGrowthMastery;